import {RpcPeerConn, ICrypto, LocalCallable, RpcOp, dumpRpcMessage, setRpcTraceReceiver, tryStringify} from './rpc.js';
import { Decoder } from 'cbor-x/decode';
import { Encoder } from 'cbor-x/encode';
import { SignalFunc } from '../signal.js';
import { WebSocket, MessageEvent } from 'ws-helper';
import { isEqualBytesConstTime } from '../../util/CoreUtils.js';
import { crypto } from 'ws-helper';
import {RpcDummyBroker} from "./rpcBroker.js";

type WsRpcIncomingCallContext = { remoteContext: any, src: string, signedWith: Uint8Array | null };
export class WebSocketRpcClient extends RpcPeerConn<{ p?: string }, WsRpcIncomingCallContext> {
    private ws: WebSocket | null;
    private websocketReconnectDelay: number = 1000;
    private websocketReconnectTimer: any = undefined;
    private keepaliveIntervalTimer: any = undefined;
    private lastMessageReceived: number = 0;
    private decoder = new Decoder({
        mapsAsObjects: true,
    });
    private encoder = new Encoder({
        mapsAsObjects: true,
    });
    private serverNonce;
    private clientNonce;
    private peerKey;
    public requestUsername : string | null;

    readonly localCallable : LocalCallable<{ remoteContext: any, signedWith: Uint8Array | null }>;

    //readonly connectionStateInfoChanged = signal<({readyState: number, bufferedAmount: number, }) => void>();
    get connectionStateInfo() {
        return {
            readyState: this.ws?.readyState, bufferedAmount: this.ws?.bufferedAmount,
            delay: this.ws?.readyState !== WebSocket.OPEN ? this.websocketReconnectDelay : null,
            outstanding: this.outstanding
        };
    }

    constructor(public readonly remoteUrl: string, crypto: ICrypto) {
        if (typeof remoteUrl !== 'string') throw 'WebSocketRpcClient: invalid remoteUrl supplied';
        const localCallable = new LocalCallable<{}>()
        super(new RpcDummyBroker<{ remoteContext: any, signedWith: Uint8Array | null }>(localCallable), crypto, remoteUrl.replace(/\//g, ':'));
        this.localCallable = localCallable;
    }
    enableLogging(logTag = 'wsRPC') {
        setRpcTraceReceiver((peer, direction, key, data) => {
            console.log(`[${logTag}:${peer}] ${direction} ` + (key ? "sigPk:" + tryStringify(key) : "") + " " + dumpRpcMessage(data));
        });
    }
    websocketDisconnect() {
        clearTimeout(this.websocketReconnectTimer);
        clearInterval(this.keepaliveIntervalTimer);
        this.websocketReconnectTimer = undefined;
        if (this.ws) {
            this.ws.onerror = this.ws.onopen = null;
            this.ws.onclose = this.ws.onclose = () => {
                this.errorOccured('WebSocket connection closed');
            };
            this.ws.close();
        }
        this.ws = null;
    }
    websocketConnect(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.websocketDisconnect();

            this.clientNonce = undefined;
            this.serverNonce = undefined;
            this.ws = new WebSocket(this.remoteUrl);
            this.ws.binaryType = 'arraybuffer';
            this.ws.onerror = (e) => {
                this.errorOccured(`WebSocket connection to ${this.remoteUrl} failed`);
                reject();
            }
            this.ws.onopen = () => {
                clearTimeout(this.websocketReconnectTimer);
                this.websocketReconnectTimer = undefined;
                this.websocketReconnectDelay = 1000;
                resolve();
            }
            this.ws.onclose = () => {
                this.errorOccured('WebSocket connection closed');
                this.websocketReconnectTimer = setTimeout(() => this.websocketConnect().catch(() => null), this.websocketReconnectDelay);
                this.websocketReconnectDelay += 1000;
                reject();
            }
            this.ws.onmessage = this.websocketOnMessage.bind(this);
            this.keepaliveIntervalTimer = setInterval(() => {
                if (this.lastMessageReceived < +new Date() - 60000) {
                    this.errorOccured('ws conn timeout'); this.ws?.terminate?.();
                    return;
                }
                this.__sendDataInternal([RpcOp.PING, +new Date()]);
            }, 30000);
        });
    }

    websocketOnMessage(e: MessageEvent) {
        this.lastMessageReceived = Date.now();
        const json = this.decoder.decode(new Uint8Array(e.data as ArrayBuffer));
        this.handleMessage(json);
    }
    handleUnwrappedMessage(json: any[], signedWith) {
        if (this.peerKey && signedWith && !isEqualBytesConstTime(this.peerKey, signedWith)) {
            console.warn("received data signed with unexpected key, ignoring");
            return;
        }
        super.handleUnwrappedMessage(json, signedWith);
    }

    buildIncomingCallContext(remoteContext: any, src: string, signedWith: Uint8Array): WsRpcIncomingCallContext {
        return { remoteContext, src, signedWith };
    }
    _sendDataInternal(data: any[]): boolean {
        if (!this.ws || this.ws.readyState != WebSocket.OPEN || this.clientNonce === undefined) {
            console.warn("failed to send, missing connection", data);
            return false;
        }
        this.ws.send(this.encoder.encode(data));
        return true;
    }

    _translateOutgoingCallContext(ctx: {p?: string}): {p?: string} {
        return ctx || {};
    }

    registerObject(objectId: string, interfaceIds: string[], handlerObject: any): Disposable {
        return this.localCallable.registerObject(objectId, interfaceIds, handlerObject);
    }
    event<T extends any[]>(objectId: string, interfaceId: string, eventName: string): SignalFunc<[{}, ...T], void> {
        return this.localCallable.event(objectId, interfaceId, eventName);
    }

    async _handlePeerHello(helloParams: any, context: WsRpcIncomingCallContext) {
        //console.log("Server Hello Received",helloParams,context)
        if (helloParams.nonce && context.signedWith && this.serverNonce === undefined) {
            this.serverNonce = helloParams.nonce;
            this.clientNonce = new Uint8Array(16);
            this.peerKey = context.signedWith;
            crypto.getRandomValues(this.clientNonce);
            const response = [RpcOp.HELLO, null, {'tokenResponse':helloParams.nonce,'nonce':this.clientNonce,'username':this.requestUsername}];
            console.log("Responding to server hello", dumpRpcMessage(response));
            this.__sendDataInternal([RpcOp.SIGNED, await this.crypto.signWithClientKey('signed', response)]);
        }
        if (helloParams.serverHelloDone) {
            if (context.signedWith && 
                    this.clientNonce && isEqualBytesConstTime(helloParams.response, this.clientNonce) &&
                    this.serverNonce && isEqualBytesConstTime(helloParams.nonceAgain, this.serverNonce)) {
                this.connected.emit(helloParams, context);
            } else {
                console.warn("invalid serverHelloDone received");
                this.ws?.close();
            }
        }
    }

    sendRpcCall(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: { p?: string }, timeout: number = 0): Promise<any> | AsyncIterator<any> {
        if (nodeId === '')
            return this.localCallable.executeRpcCall(opCode, objectId, interfaceId, method, args, { remoteContext: { p: context?.p || '' }, signedWith: null, }, timeout);
        else
            return super.sendRpcCall(opCode, nodeId, objectId, interfaceId, method, args, context, timeout);
    }
    sendRpcCallNoResponse(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: { p?: string }) {
        if (nodeId === '')
            this.localCallable.executeRpcCall(opCode, objectId, interfaceId, method, args, { remoteContext: { p: context?.p || '' }, signedWith: null, });
        else
            super.sendRpcCallNoResponse(opCode, nodeId, objectId, interfaceId, method, args, context);
    }



    // backwards compatibility:

    /**
     * @deprecated use sendRpcCall or higher level methods instead
     */
    _rpcCall(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: {p?: string}, timeout: number = 0): Promise<any> | AsyncIterator<any> {
        return this.sendRpcCall(opCode, nodeId, objectId, interfaceId, method, args, context, timeout);
    }
    /**
     * @deprecated use sendRpcCallNoResponse or higher level methods instead
     */
    _rpcCallNoResponse(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: {p?: string}) {
        this.sendRpcCallNoResponse(opCode, nodeId, objectId, interfaceId, method, args, context);
    }


}